import React, { useEffect, useMemo } from "react";
import { EmblaOptionsType } from "embla-carousel";
import styled from "@emotion/styled/macro";
import useEmblaCarousel from "embla-carousel-react";
import {
  useSingletonMobileMediaQuery,
  useSingletonTabletMediaQuery,
} from "@sunrun/experience-ui-components";
import {
  DotButton,
  EmblaDots,
  NextButton,
  PrevButton,
  useDotButton,
  usePrevNextButtons,
} from "../atoms/emblaAtoms";

type PropType = {
  imageUrls: string[];
  options?: EmblaOptionsType;
  showDots?: boolean;
  requestedIndex: number;
};

const FullSizeCarousel: React.FC<PropType> = (props) => {
  const { imageUrls, requestedIndex, options } = props;
  const [ref, api] = useEmblaCarousel(options);

  const slides = useMemo(
    () =>
      imageUrls.map((url, index) => (
        <FullSizePhotoSlide key={index} src={url} alt="" />
      )),
    [imageUrls]
  );

  useEffect(() => {
    if (requestedIndex > -1) {
      api?.scrollTo(requestedIndex);
    }
  }, [api, requestedIndex]);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(api);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(api);

  const isMobile = useSingletonMobileMediaQuery();
  const isTablet = useSingletonTabletMediaQuery();

  return (
    <CustomEmbla>
      <RelativeContainer>
        {slides.length > 1 && (
          <EmblaButtonContainer isMobile={isMobile} isTablet={isTablet}>
            <PrevButton
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
            />
            <NextButton
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
            />
          </EmblaButtonContainer>
        )}
        <CustomViewport ref={ref}>
          <CustomContainer>
            {slides.map((child, index) => (
              <CustSlideContainer
                key={index}
                isSelected={index === selectedIndex}
              >
                <CustSlide onClick={() => onDotButtonClick(index)}>
                  <SlideScrollArea>{child}</SlideScrollArea>
                </CustSlide>
              </CustSlideContainer>
            ))}
          </CustomContainer>
        </CustomViewport>
      </RelativeContainer>
      <EmblaDots>
        {scrollSnaps.map((_, index) => (
          <DotButton
            key={index}
            onClick={() => onDotButtonClick(index)}
            className={"embla__dot".concat(
              index === selectedIndex
                ? " embla__dot--selected full-size"
                : " full-size"
            )}
          />
        ))}
      </EmblaDots>
    </CustomEmbla>
  );
};

const EmblaButtonContainer = styled.div<{
  isMobile: boolean;
  isTablet: boolean;
}>`
  display: flex;
  width: 100%;
  max-width: 1100px;
  justify-content: space-between;
  position: absolute;
  top: ${({ isMobile, isTablet }) =>
    isMobile || isTablet ? "20%" : "calc(50% - 15px)"};
  left: 50%;
  transform: translate(-50%);
  z-index: 1;
`;

const RelativeContainer = styled.div`
  position: relative;
`;

const CustomEmbla = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CustomContainer = styled.div`
  touch-action: pan-y pinch-zoom;
  display: flex;
`;

const CustomViewport = styled.div`
  overflow: hidden;
  width: 100%;
`;

const SlideScrollArea = styled.div`
  max-height: calc(98vh - 120px);
  overflow-x: hidden;
  overflow-y: auto;
`;

const CustSlide = styled.div`
  flex: 0 0 100%;
  min-width: 0;
  width: 100%;
`;

const CustSlideContainer = styled.div<{ isSelected: boolean }>`
  flex: 0 0 100%;
  min-width: 0;
  width: 100%;
  ${({ isSelected }) =>
    !isSelected &&
    `
    height: 0;
    overflow: hidden;
    visibility: hidden;
    pointer-events: none;
  `}
`;

const FullSizePhotoSlide = styled.img`
  max-width: 100%;
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
`;

export { FullSizeCarousel };
